import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { I18nProvider } from './context/I18nContext';
import './App.css';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';

// Driver Pages
import DriverLayout from './layouts/DriverLayout';
import DriverDashboard from './pages/driver/Dashboard';
import DriverOnboarding from './pages/driver/Onboarding';
import AvailableRides from './pages/driver/AvailableRides';
import ActiveRides from './pages/driver/ActiveRides';
import DriverEarnings from './pages/driver/Earnings';
import DriverProfile from './pages/driver/Profile';
import DriverReferral from './pages/driver/Referral';
import DriverLoyalty from './pages/driver/Loyalty';
import MusicPlayer from './pages/driver/MusicPlayer';
import Payouts from './pages/driver/Payouts';
import Schedule from './pages/driver/Schedule';
import DriverBackgroundCheck from './pages/driver/BackgroundCheck';

// Rider Pages
import RiderLayout from './layouts/RiderLayout';
import RiderDashboard from './pages/rider/Dashboard';
import BookRide from './pages/rider/BookRide';
import ScheduleRide from './pages/rider/ScheduleRide';
import RideHistory from './pages/rider/RideHistory';
import RideDetails from './pages/rider/RideDetails';
import PaymentMethods from './pages/rider/PaymentMethods';
import LoyaltyRewards from './pages/rider/LoyaltyRewards';
import RiderProfile from './pages/rider/Profile';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';

// Static Pages
import Safety from './pages/Safety';
import DriverRequirements from './pages/DriverRequirements';
import Earnings from './pages/Earnings';
import About from './pages/About';
import Careers from './pages/Careers';
import Contact from './pages/Contact';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Help from './pages/Help';

// Protected Route Component
const ProtectedRoute = ({ children, userType }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-red-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (userType && user?.user_type !== userType) {
    return <Navigate to={user?.user_type === 'driver' ? '/driver' : '/rider'} replace />;
  }

  return children;
};

// Public Route (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-red-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={user?.user_type === 'driver' ? '/driver' : '/rider'} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      
      {/* Static Pages */}
      <Route path="/safety" element={<Safety />} />
      <Route path="/driver-requirements" element={<DriverRequirements />} />
      <Route path="/earnings" element={<Earnings />} />
      <Route path="/about" element={<About />} />
      <Route path="/careers" element={<Careers />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/help" element={<Help />} />

      {/* Driver Routes */}
      <Route path="/driver/onboarding" element={
        <ProtectedRoute userType="driver">
          <DriverOnboarding />
        </ProtectedRoute>
      } />
      <Route path="/driver" element={
        <ProtectedRoute userType="driver">
          <DriverLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DriverDashboard />} />
        <Route path="rides" element={<AvailableRides />} />
        <Route path="active" element={<ActiveRides />} />
        <Route path="earnings" element={<DriverEarnings />} />
        <Route path="payouts" element={<Payouts />} />
        <Route path="schedule" element={<Schedule />} />
        <Route path="background-check" element={<DriverBackgroundCheck />} />
        <Route path="loyalty" element={<DriverLoyalty />} />
        <Route path="referral" element={<DriverReferral />} />
        <Route path="music" element={<MusicPlayer />} />
        <Route path="profile" element={<DriverProfile />} />
      </Route>

      {/* Rider Routes */}
      <Route path="/rider" element={
        <ProtectedRoute userType="rider">
          <RiderLayout />
        </ProtectedRoute>
      }>
        <Route index element={<RiderDashboard />} />
        <Route path="book" element={<BookRide />} />
        <Route path="schedule" element={<ScheduleRide />} />
        <Route path="history" element={<RideHistory />} />
        <Route path="ride/:rideId" element={<RideDetails />} />
        <Route path="payments" element={<PaymentMethods />} />
        <Route path="loyalty" element={<LoyaltyRewards />} />
        <Route path="profile" element={<RiderProfile />} />
      </Route>

      {/* Admin Routes - accessible without login for registration */}
      <Route path="/admin" element={<AdminDashboard />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;
